import React, { useState, useEffect, useRef } from 'react';
import { companyAPI, fileUrl } from '../../services/api';
import { updateCachedCompany, getCachedCompany } from '../common/useCompany';
import { setDisplayCurrency } from '../../utils/currency';
import toast from 'react-hot-toast';
import { Upload } from 'lucide-react';
export default function CompanySettings() {
  const [company, setCompany] = useState({}); const [currencies, setCurrencies] = useState([]); const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoRef = useRef(null);
  useEffect(() => {
    companyAPI.current().then(setCompany).catch(e=>toast.error(e.message));
    companyAPI.currencies().then(setCurrencies).catch(()=>{});
    Promise.all([companyAPI.current(), companyAPI.currencies()]).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) { toast.error('Please choose a PNG, JPG, WEBP or GIF image.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB).'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setLogoBusy(true);
      try {
        const res = await companyAPI.uploadLogo(reader.result);
        setCompany({ ...company, logo_path: res.logo_path });
        toast.success('Logo uploaded');
      } catch (err) { toast.error(err.message || 'Upload failed'); }
      setLogoBusy(false);
    };
    reader.readAsDataURL(file);
  };
  const handleSubmit = async(e) => { e.preventDefault(); setSaving(true);
    try {
      const prevBase = getCachedCompany()?.base_currency;
      await companyAPI.update(company);
      if (company.base_currency && company.base_currency !== prevBase) {
        // Apply the new base currency instantly across all open screens
        setDisplayCurrency(company.base_currency);
        updateCachedCompany({ base_currency: company.base_currency });
      }
      toast.success('Company updated');
    } catch(err){toast.error(err.message);} setSaving(false); };
  const u = (f) => (e) => setCompany({...company, [f]: e.target.value});
  if (loading) return <div className="spinner" style={{margin:'40px auto'}} />;
  return (
    <div>
      <div className="toolbar"><h2>Company Settings</h2></div>
      <div className="card" style={{marginBottom:16}}><div className="card-body">
        <h3 style={{marginBottom:12}}>Company Logo</h3>
        <div style={{display:'flex',gap:16,alignItems:'center'}}>
          {company.logo_path
            ? <img src={fileUrl(company.logo_path)} alt="Logo" style={{maxWidth:140,maxHeight:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,padding:4}} />
            : <div style={{width:140,height:56,border:'1px dashed var(--border-dark)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-light)',fontSize:12}}>No logo</div>}
          <div>
            <button type="button" className="btn btn-outline btn-sm" disabled={logoBusy} onClick={() => logoRef.current && logoRef.current.click()}>
              <Upload size={14} style={{verticalAlign:'-2px',marginRight:4}} /> {logoBusy ? 'Uploading…' : 'Upload Logo'}
            </button>
            <div style={{fontSize:11,color:'var(--text-light)',marginTop:6}}>PNG, JPG, WEBP or GIF · max 5 MB · printed on invoices, estimates &amp; reports</div>
            <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{display:'none'}} onChange={uploadLogo} />
          </div>
        </div>
      </div></div>
      <div className="card"><div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group"><label>Company Name <span className="required">*</span></label><input className="form-control" required value={company.name||''} onChange={u('name')}/></div>
            <div className="form-group"><label>Legal Name</label><input className="form-control" value={company.legal_name||''} onChange={u('legal_name')}/></div>
            <div className="form-group"><label>Tax ID</label><input className="form-control" value={company.tax_id||''} onChange={u('tax_id')}/></div>
            <div className="form-group"><label>Registration #</label><input className="form-control" value={company.registration_number||''} onChange={u('registration_number')}/></div>
            <div className="form-group full-width"><label>Address</label><input className="form-control" value={company.address_line1||''} onChange={u('address_line1')}/></div>
            <div className="form-group"><label>City</label><input className="form-control" value={company.city||''} onChange={u('city')}/></div>
            <div className="form-group"><label>State</label><input className="form-control" value={company.state||''} onChange={u('state')}/></div>
            <div className="form-group"><label>Postal Code</label><input className="form-control" value={company.postal_code||''} onChange={u('postal_code')}/></div>
            <div className="form-group"><label>Country</label><input className="form-control" value={company.country||''} onChange={u('country')}/></div>
            <div className="form-group"><label>Phone</label><input className="form-control" value={company.phone||''} onChange={u('phone')}/></div>
            <div className="form-group"><label>Email</label><input className="form-control" type="email" value={company.email||''} onChange={u('email')}/></div>
            <div className="form-group"><label>Website</label><input className="form-control" value={company.website||''} onChange={u('website')}/></div>
            <div className="form-group"><label>Base Currency</label><select className="form-control" value={company.base_currency||'USD'} onChange={u('base_currency')}>
              {(currencies.length ? currencies : [{code:'USD',name:'US Dollar'},{code:'EUR',name:'Euro'},{code:'GBP',name:'British Pound'},{code:'CAD',name:'Canadian Dollar'}]).map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}</select>
              <div style={{fontSize:11,color:'var(--text-light)',marginTop:4}}>Manage currencies &amp; exchange rates in <a href="#/settings/currency">Currencies &amp; Exchange Rates</a></div></div>
            <div className="form-group"><label>Fiscal Year Start Month</label><select className="form-control" value={company.fiscal_year_start_month||1} onChange={u('fiscal_year_start_month')}>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m=><option key={m} value={m}>{m}</option>)}</select></div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Save Changes'}</button>
          </div>
        </form>
      </div></div>
    </div>
  );
}
